import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fhn_admin_token";

const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// --------------------------------------------------------------------------
// Login
// --------------------------------------------------------------------------
function Login() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) nav("/admin", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success(`Welcome, ${data.username}`);
      nav("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-neutral-900 border border-neutral-800 rounded-2xl p-8 shadow-2xl"
        data-testid="login-form"
      >
        <h1 className="text-2xl font-semibold tracking-tight mb-1">theFHNapp admin</h1>
        <p className="text-sm text-neutral-400 mb-6">Sign in to edit the landing page.</p>

        <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Username</label>
        <input
          data-testid="login-username-input"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 mb-4 outline-none focus:border-amber-500"
          autoComplete="username"
          required
        />

        <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Password</label>
        <input
          data-testid="login-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 mb-6 outline-none focus:border-amber-500"
          autoComplete="current-password"
          required
        />

        <button
          data-testid="login-submit-btn"
          disabled={loading}
          className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-neutral-950 font-semibold rounded-lg py-2.5 transition-colors"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <a
          href="/"
          className="block mt-6 text-center text-xs text-neutral-500 hover:text-neutral-300"
          data-testid="view-site-link"
        >
          ← View public site
        </a>
      </form>
    </div>
  );
}

// --------------------------------------------------------------------------
// Dashboard
// --------------------------------------------------------------------------
function Dashboard() {
  const nav = useNavigate();
  const [content, setContent] = useState("");
  const [initialContent, setInitialContent] = useState("");
  const [note, setNote] = useState("");
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/admin/login", { replace: true });
  }, [nav]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // Verify session first
      await axios.get(`${API}/auth/me`, { headers: authHeader() });
      const [htmlRes, versRes] = await Promise.all([
        axios.get(`${API}/html`),
        axios.get(`${API}/html/versions`, { headers: authHeader() }),
      ]);
      setContent(htmlRes.data.content);
      setInitialContent(htmlRes.data.content);
      setVersions(versRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error(err?.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      nav("/admin/login", { replace: true });
      return;
    }
    loadAll();
  }, [loadAll, nav]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/html`,
        { content, note: note.trim() || null },
        { headers: authHeader() }
      );
      toast.success("Saved. New version is now live.");
      setNote("");
      setInitialContent(content);
      const versRes = await axios.get(`${API}/html/versions`, { headers: authHeader() });
      setVersions(versRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const loadVersion = async (versionId) => {
    try {
      const { data } = await axios.get(`${API}/html/versions/${versionId}`, {
        headers: authHeader(),
      });
      setContent(data.content);
      toast.message("Loaded into editor. Click Save to publish.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const restoreVersion = async (versionId) => {
    if (!window.confirm("Restore this version as the live site?")) return;
    try {
      const { data } = await axios.post(
        `${API}/html/versions/${versionId}/restore`,
        {},
        { headers: authHeader() }
      );
      setContent(data.content);
      setInitialContent(data.content);
      toast.success("Version restored and published.");
      const versRes = await axios.get(`${API}/html/versions`, { headers: authHeader() });
      setVersions(versRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const dirty = content !== initialContent;

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-400 flex items-center justify-center">
        Loading editor…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col">
      <header className="border-b border-neutral-800 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">theFHNapp — HTML editor</h1>
          <p className="text-xs text-neutral-500">
            Edit the full HTML below. Save to publish a new version live.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="text-sm text-neutral-400 hover:text-amber-400"
            data-testid="open-live-site-link"
          >
            View live site ↗
          </a>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="text-sm px-3 py-1.5 rounded-lg border border-neutral-800 hover:border-neutral-600"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-0">
        {/* Editor */}
        <div className="flex flex-col p-6 gap-3 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Change note (optional) — e.g. 'Updated CTA copy'"
              className="flex-1 min-w-[200px] bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
            />
            <button
              onClick={() => setPreviewOpen(true)}
              data-testid="preview-btn"
              className="px-4 py-2 rounded-lg border border-neutral-800 hover:border-neutral-600 text-sm"
            >
              Preview
            </button>
            <button
              onClick={save}
              disabled={saving || !dirty}
              data-testid="save-btn"
              className="px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-neutral-950 font-semibold text-sm"
            >
              {saving ? "Saving…" : dirty ? "Save & publish" : "Saved"}
            </button>
          </div>

          <textarea
            data-testid="html-editor"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            spellCheck={false}
            className="flex-1 min-h-[60vh] w-full bg-neutral-900 border border-neutral-800 rounded-xl p-4 font-mono text-xs leading-5 text-neutral-100 outline-none focus:border-amber-500 resize-none"
          />
          <p className="text-xs text-neutral-500">
            {content.length.toLocaleString()} characters {dirty && "• unsaved changes"}
          </p>
        </div>

        {/* Version history */}
        <aside className="border-t lg:border-t-0 lg:border-l border-neutral-800 p-6 bg-neutral-925 lg:min-h-full">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-3">
            Version history
          </h2>
          <p className="text-xs text-neutral-500 mb-4">
            The most recent {versions.length} of the last 10 saves. Load to preview in the editor,
            or restore to publish.
          </p>
          <ol className="space-y-3" data-testid="versions-list">
            {versions.map((v, idx) => (
              <li
                key={v.id}
                className="bg-neutral-900 border border-neutral-800 rounded-lg p-3"
                data-testid={`version-item-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-400">
                    {new Date(v.updated_at).toLocaleString()}
                  </span>
                  {idx === 0 && (
                    <span className="text-[10px] uppercase text-amber-400 tracking-wider">
                      Live
                    </span>
                  )}
                </div>
                {v.note && <p className="text-sm text-neutral-200 mt-1">{v.note}</p>}
                <p className="text-[11px] text-neutral-500 mt-1">by {v.updated_by}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => loadVersion(v.id)}
                    className="text-xs px-2 py-1 rounded border border-neutral-800 hover:border-neutral-600"
                    data-testid={`load-version-${idx}`}
                  >
                    Load
                  </button>
                  {idx !== 0 && (
                    <button
                      onClick={() => restoreVersion(v.id)}
                      className="text-xs px-2 py-1 rounded border border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                      data-testid={`restore-version-${idx}`}
                    >
                      Restore
                    </button>
                  )}
                </div>
              </li>
            ))}
            {versions.length === 0 && (
              <li className="text-sm text-neutral-500">No versions yet.</li>
            )}
          </ol>
        </aside>
      </div>

      {previewOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 p-4 flex flex-col" data-testid="preview-modal">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-neutral-300">
              Preview (unsaved edits). Not yet live.
            </p>
            <button
              onClick={() => setPreviewOpen(false)}
              className="text-sm px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700"
              data-testid="close-preview-btn"
            >
              Close preview
            </button>
          </div>
          <iframe
            title="preview"
            srcDoc={content}
            className="flex-1 w-full bg-white rounded-xl border border-neutral-800"
          />
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// App
// --------------------------------------------------------------------------
function AdminShell() {
  return (
    <BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
      <Routes>
        <Route path="/admin/login" element={<Login />} />
        <Route path="/admin" element={<Dashboard />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  // The React app should only render for /admin routes; the public site is
  // rendered directly from public/index.html via document.write. Guard here
  // in case bootstrap failed.
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin")) {
    return null;
  }
  return <AdminShell />;
}

export default App;
